import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, asyncScheduler, map, observeOn, of, tap } from 'rxjs';
import {
  IAccessRoleMatrix,
  IAppStandardResponse,
  ILanguageResponse,
  IProfileDetail,
  IProfileSavePayload,
  IProfileUserRole,
  ISaveProfileResponse,
  IRequestAccessRolesResponse,
  IDepartmentLvlManager,
  IDepartmentLvlManagerPayload,
} from '../interfaces/profile-interface';
import { IGetCommomInsertUpdateResponse } from '../interfaces/common';

// Broader scopes rank first so the widest role drives the flat profile fields.
const ROLE_LEVEL_RANK: Record<string, number> = {
  GLOBAL: 0,
  SEGMENT: 1,
  DIVISION: 2,
  DEPARTMENT: 3,
};

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private apiUrl = environment.apiUrl;
  private readonly managerCache = new Map<
    string,
    { expiresAt: number; response: IDepartmentLvlManager[] }
  >();

  constructor(private http: HttpClient) {}

  getProfileDetails(userMail: string): Observable<IProfileDetail> {
    return this.http
      .get<IProfileDetail>(
        `${this.apiUrl}/api/MyProfile/GetMyProfileByMail/${userMail}`,
      )
      .pipe(map((response) => this.normalizeProfile(response)));
  }

  /**
   * Flattens the multi-role response onto the legacy single-role fields the
   * rest of the app reads (role, roleCode, levelName, segment, division...).
   * The broadest-scoped role is treated as the primary one.
   */
  private normalizeProfile(response: IProfileDetail): IProfileDetail {
    const source = response as IProfileDetail & {
      AccessRoleMatrix?: IAccessRoleMatrix;
    };
    const roles = source.roles ?? [];
    const primary = this.pickPrimaryRole(roles);
    const hasRoles = roles.length > 0;

    return {
      ...source,
      accessRoleMatrix:
        source.accessRoleMatrix ??
        source.AccessRoleMatrix ??
        ({} as IAccessRoleMatrix),
      hrId: source.hrId ?? source.hrid ?? null,
      hrid: source.hrid ?? source.hrId ?? null,
      isSWPUser: source.isSWPUser ?? false,
      role: hasRoles
        ? (source.roleNames ?? roles.map((r) => r.role))
            .filter(Boolean)
            .join(', ')
        : source.role,
      roleCode: hasRoles
        ? (source.roleCodes ?? roles.map((r) => r.roleCode))
            .filter(Boolean)
            .join(',')
        : source.roleCode,
      levelName: primary?.levelName ?? source.levelName,
      // Filter preselection uses the user's own HR org, not the role scope.
      segment:
        source.snowFlakeSegment || primary?.roleSegment || source.segment || '',
      division:
        source.snowFlakeDivision ||
        primary?.roleDivision ||
        source.division ||
        '',
      segmentCode:
        source.snowFlakeSegmentCode ??
        primary?.roleSegmentCode ??
        source.segmentCode,
      divisionCode:
        source.snowFlakeDivisionCode ??
        primary?.roleDivisionCode ??
        source.divisionCode,
      roleSegment: primary?.roleSegment ?? source.roleSegment,
      roleDivision: primary?.roleDivision ?? source.roleDivision,
      roleSegmentCode: primary?.roleSegmentCode ?? source.roleSegmentCode,
      roleDivisionCode: primary?.roleDivisionCode ?? source.roleDivisionCode,
      roleSegmentId: primary?.roleSegmentId ?? source.roleSegmentId ?? null,
      roleDivisionId: primary?.roleDivisionId ?? source.roleDivisionId ?? null,
      roleDepartments: source.departments?.length
        ? source.departments
        : primary?.roleDepartment
          ? [primary.roleDepartment]
          : (source.roleDepartments ?? []),
      roleDepartmentCodes: primary?.roleDepartmentCode
        ? [primary.roleDepartmentCode]
        : (source.roleDepartmentCodes ?? []),
    };
  }

  private pickPrimaryRole(
    roles: IProfileUserRole[],
  ): IProfileUserRole | undefined {
    const rank = (role: IProfileUserRole) =>
      ROLE_LEVEL_RANK[(role.levelName ?? '').toUpperCase()] ?? 99;
    return [...roles].sort((a, b) => rank(a) - rank(b))[0];
  }

  getProfileLanguages(): Observable<ILanguageResponse> {
    return this.http.get<ILanguageResponse>(
      `${this.apiUrl}/api/AppMasterData/GetLanguages`,
      {},
    );
  }
  getAllSiteCodes(isAll: boolean = true): Observable<IAppStandardResponse> {
    const all = isAll ? '?siteFilter=All' : '';
    return this.http.get<IAppStandardResponse>(`${this.apiUrl}/SiteCode${all}`);
  }

  getCountries(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/Countries`);
  }

  getStates(countryId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/States?CountryId=${countryId}`);
  }

  getCities(stateId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/Cities?StateId=${stateId}`);
  }

  getWorkLocationDetails(
    countryId: string,
    stateId: string = '0',
    cityId: string = '0',
  ): Observable<any> {
    let queryParams = `countryId=${countryId}`;

    if (stateId && stateId !== '0') {
      queryParams += `&stateId=${stateId}`;
    }

    if (cityId && cityId !== '0') {
      queryParams += `&cityId=${cityId}`;
    }

    return this.http.get<any>(
      `${this.apiUrl}/GetWorkLocationDetails?${queryParams}`,
    );
  }

  updateProfile(
    payload: IProfileSavePayload,
  ): Observable<ISaveProfileResponse> {
    return this.http.post<ISaveProfileResponse>(
      `${this.apiUrl}/api/MyProfile/SaveProfileDetails`,
      payload,
    );
  }

  getRequestAccessRoles(
    roleLevelId: number,
  ): Observable<IRequestAccessRolesResponse> {
    return this.http.get<IRequestAccessRolesResponse>(
      `${this.apiUrl}/api/MyProfile/GetRequestAccessRoles?RoleLevelId=${roleLevelId}`,
    );
  }

  getDepartmentLvlManagers(
    payload: IDepartmentLvlManagerPayload,
    cacheNamespace?: string,
  ): Observable<IDepartmentLvlManager[]> {
    const request = () =>
      this.http.post<IDepartmentLvlManager[]>(
        `${this.apiUrl}/api/MyProfile/GetDepartmentLvlManagers`,
        payload,
      );
    if (!cacheNamespace) {
      return request();
    }

    const key = `${cacheNamespace}:${JSON.stringify(payload)}`;
    const cached = this.managerCache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return of(cached.response).pipe(observeOn(asyncScheduler));
    }
    this.managerCache.delete(key);

    return request().pipe(
      tap((response) => {
        if (this.managerCache.size >= 50) {
          const oldestKey = this.managerCache.keys().next().value;
          if (oldestKey !== undefined) {
            this.managerCache.delete(oldestKey);
          }
        }
        this.managerCache.set(key, {
          expiresAt: Date.now() + 5 * 60 * 1000,
          response,
        });
      }),
    );
  }
}
